import PropTypes from "prop-types";
import * as React from "react";
import { useInView } from "react-intersection-observer";
import { Skeleton } from "../../../themeV2";
import { Influencer } from "../../../domain";
import { useInfluencers } from "../../../hooks";
import * as constants from "../constants";
import SectionExpanded from "./SectionExpanded";
import ShopsSlider from "./ShopsSlider";
import css from "./Section.module.css";

const SKELETON_COUNT = 6;

const normalizeProduct = (product) => ({
  ...product,
  _totalPrice: product.price + (product.product_bump || 0),
  _vaNames: (product.fulfillers || []).map((f) => f.name).filter(Boolean),
  _isHolo: /holo|gold foil|metallic|gold/i.test(product.name),
});

function enricherFor(type) {
  if (type === "influencer") {
    return (results) => results.map(Influencer.from);
  }

  return (results) => results.map(normalizeProduct);
}

function Section({
  section: { name, href, title, type, look },
  isMobile,
  updateFavouriteStatus,
}) {
  const { ref, inView } = useInView({ threshold: 0.01 });

  const { results, isLoading, refresh } = useInfluencers(
    inView,
    name,
    enricherFor(type),
  );

  const wrappedUpdateFavoriteStatus = React.useCallback(
    (influencer, callback) => {
      updateFavouriteStatus(influencer, (isFavourite) => {
        if (!isFavourite && name.startsWith("favourite")) {
          refresh();
        } else {
          return callback(isFavourite);
        }
      });
    },
    [updateFavouriteStatus, name, refresh],
  );

  const [isExpanded, setIsExpanded] = React.useState(false);

  if (isLoading && !results?.length) {
    const cardWidth = constants.cardWidthFor({ isMobile, look });
    const cardHeight = constants.cardHeightFor({ isMobile, look });

    return (
      <div className={css.section}>
        <div className={css.flexBetween} ref={ref}>
          <h3 className={css.sectionTitle}>{title}</h3>
        </div>

        <div className={css.loadingGrid}>
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rect"
              width={cardWidth}
              height={cardHeight}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!isLoading && !results?.length) {
    return null;
  }

  return (
    <div className={css.section}>
      <div className={css.flexBetween} ref={ref}>
        <h3 className={css.sectionTitle}>
          {href ? <a href={href}>{title}</a> : title}
        </h3>

        {Boolean(href) && (
          <a className={css.textButton} href={href}>
            View All
          </a>
        )}

        {!href && results?.length > SKELETON_COUNT && (
          <button
            className={css.textButton}
            onClick={() => setIsExpanded((s) => !s)}
          >
            {isExpanded ? "Show Less" : "Show More"}
          </button>
        )}
      </div>

      {!isExpanded && (
        <ShopsSlider
          products={type === constants.PRODUCT_TYPE ? results : undefined}
          influencers={type === constants.INFLUENCER_TYPE ? results : undefined}
          updateFavouriteStatus={
            updateFavouriteStatus ? wrappedUpdateFavoriteStatus : undefined
          }
          look={look}
          isMobile={isMobile}
        />
      )}

      {isExpanded && (
        <SectionExpanded
          products={type === constants.PRODUCT_TYPE ? results : undefined}
          influencers={type === constants.INFLUENCER_TYPE ? results : undefined}
          updateFavouriteStatus={
            updateFavouriteStatus ? wrappedUpdateFavoriteStatus : undefined
          }
          isMobile={isMobile}
          look={look}
        />
      )}
    </div>
  );
}

Section.propTypes = {
  section: PropTypes.shape({
    type: PropTypes.oneOf([constants.PRODUCT_TYPE, constants.INFLUENCER_TYPE]),
    name: PropTypes.string,
    title: PropTypes.string,
    look: PropTypes.oneOf([
      constants.LOOK_TALL,
      constants.LOOK_WIDE,
      constants.LOOK_PRODUCT,
    ]),
    href: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
  updateFavouriteStatus: PropTypes.func,
};

export default React.memo(Section);
